import { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

type CardLike = {
  name: string;
  image_url?: string | null;
  mana_cost?: string;
  type_line?: string;
};

/**
 * Anchor-wrapper that pops a larger card image when the child is hovered or
 * focused. Falls back to a text-only chip when no image_url is available.
 *
 * The popover is position-adjusted against the viewport so it never clips
 * offscreen when used inside the deck-list grid.
 */
// Approx. rendered popover dimensions used for viewport clamping. Slightly
// generous so the bubble never kisses the screen edge before the math fires.
const POP_W = 252;
const POP_H = 352;
const PAD = 12;
const CURSOR_OFFSET = 18;

function clampToViewport(
  cursorX: number,
  cursorY: number,
): { top: number; left: number } {
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  // Default: render to the right of the cursor.
  let left = cursorX + CURSOR_OFFSET;
  // If we'd overflow the right edge, flip to the left of the cursor instead.
  if (left + POP_W + PAD > vw) {
    left = cursorX - CURSOR_OFFSET - POP_W;
  }
  // Final clamp inside the viewport in case the cursor is right at an edge.
  left = Math.max(PAD, Math.min(left, vw - POP_W - PAD));

  // Center the bubble vertically on the cursor, then clamp.
  let top = cursorY - POP_H / 2;
  top = Math.max(PAD, Math.min(top, vh - POP_H - PAD));
  return { top, left };
}

export function CardHover({
  card,
  children,
  className,
}: {
  card?: CardLike | null;
  children: React.ReactNode;
  className?: string;
}) {
  const hasImage = !!card?.image_url;
  const [open, setOpen] = useState(false);
  const [placement, setPlacement] = useState<{ top: number; left: number } | null>(null);
  const anchorRef = useRef<HTMLSpanElement | null>(null);

  const updateFromAnchor = useCallback(() => {
    const el = anchorRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    // Default anchor: just past the right edge, vertically centered on the
    // text. This is what gets used for keyboard-focus (no cursor available).
    setPlacement(clampToViewport(rect.right + 4, rect.top + rect.height / 2));
  }, []);

  const onMouseEnter = (e: React.MouseEvent) => {
    setOpen(true);
    setPlacement(clampToViewport(e.clientX, e.clientY));
  };
  const onMouseMove = (e: React.MouseEvent) => {
    if (!open) return;
    setPlacement(clampToViewport(e.clientX, e.clientY));
  };
  const onMouseLeave = () => setOpen(false);
  const onFocus = () => {
    setOpen(true);
    updateFromAnchor();
  };
  const onBlur = () => setOpen(false);

  // Re-clamp on resize so the popover never escapes after a window change.
  useEffect(() => {
    if (!open) return;
    const onResize = () => {
      setPlacement((p) => (p ? clampToViewport(p.left, p.top + POP_H / 2) : p));
    };
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [open]);

  if (!hasImage || !card) {
    return <span className={className}>{children}</span>;
  }

  return (
    <span
      ref={anchorRef}
      className={`card-hover ${className ?? ""}`.trim()}
      onMouseEnter={onMouseEnter}
      onMouseMove={onMouseMove}
      onMouseLeave={onMouseLeave}
      onFocus={onFocus}
      onBlur={onBlur}
      tabIndex={0}
    >
      {children}
      {open && placement &&
        createPortal(
          <span
            className="card-hover-pop"
            style={{ top: placement.top, left: placement.left }}
            role="tooltip"
          >
            <img
              src={card.image_url!}
              alt={card.name}
              width={240}
              loading="lazy"
              draggable={false}
            />
          </span>,
          document.body,
        )}
    </span>
  );
}

/**
 * Static thumbnail used inside the gallery view and the build-around chip.
 */
export function CardThumb({
  card,
  size = "md",
  onClick,
  selected,
}: {
  card: CardLike;
  size?: "sm" | "md" | "lg";
  onClick?: () => void;
  selected?: boolean;
}) {
  const widths = { sm: 100, md: 160, lg: 220 };
  const width = widths[size];
  const cls = `card-thumb card-thumb-${size}${selected ? " selected" : ""}${onClick ? " clickable" : ""}`;
  const content = card.image_url ? (
    <img src={card.image_url} alt={card.name} width={width} loading="lazy" />
  ) : (
    <div className="card-thumb-fallback">
      <div className="card-thumb-name">{card.name}</div>
      {card.type_line && <div className="card-thumb-type">{card.type_line}</div>}
    </div>
  );
  if (onClick) {
    return (
      <button type="button" className={cls} onClick={onClick} title={card.name}>
        {content}
      </button>
    );
  }
  return (
    <span className={cls} title={card.name}>
      {content}
    </span>
  );
}
